"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Award, CircleCheck as CheckCircle2, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const trustBadges = [{
    id: "design-build",
    label: "Full-Service Design-Build",
    icon: CheckCircle2
  }, {
    id: "local-team",
    label: "Local Brookhaven Team",
    icon: MapPin
  }];
  return <section id="herointro" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-28 pb-20 md:pt-36 md:pb-28 lg:pt-40 lg:pb-32">
      {/* Subtle architectural background texture accent */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Narrative Zone (5 Columns on Desktop) */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col items-start">
              {/* Category / Location Eyebrow */}
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-secondary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>Brookhaven, GA • Residential Architectural Renovations</span>
              </div>

              {/* Main Heading (SEO H1) */}
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[2.85rem] font-semibold leading-[1.15] tracking-tight text-foreground">
                Whole-Home &amp; Interior Remodeling in Brookhaven, GA
              </h1>

              {/* Architectural Gold Hairline */}
              <div className="mt-6 h-[2px] w-20 bg-primary/60 rounded-full" aria-hidden="true" />

              {/* Lead Paragraph */}
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-foreground/80">
                We renew historic estates and established Brookhaven homes with structural precision, open-concept spaces, and bespoke architectural millwork tailored to your lifestyle.
              </p>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
                <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground font-semibold px-7 py-3.5 shadow-md transition-[color,background-color,box-shadow,transform] duration-200 hover:bg-primary/90 hover:-translate-y-0.5">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an Estimate
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="rounded-lg border-border bg-background text-foreground font-medium px-6 py-3.5 shadow-xs transition-[color,background-color,border-color,transform] duration-200 hover:border-primary/50 hover:bg-muted/50 hover:-translate-y-0.5">
                  <Link to="/OurProjectsPage" className="inline-flex items-center justify-center gap-2">
                    <span>Explore Gallery</span>
                    <ArrowUpRight className="size-4 text-foreground/70" />
                  </Link>
                </Button>
              </div>

              {/* Trust Credentials */}
              <div className="mt-10 pt-8 border-t border-border/80 w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                {trustBadges.map((badge, i) => {
                const Icon = badge.icon;
                return <div key={badge.id} data-index={i} className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-secondary">
                        <Icon className="size-4" />
                      </div>
                      <span className="text-xs font-medium text-foreground/80 leading-snug">
                        {badge.label}
                      </span>
                    </div>;
              })}
              </div>
            </motion.div>
          </div>

          {/* Right Visual Zone (7 Columns on Desktop) */}
          <div className="lg:col-span-7 relative">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative">
              {/* Main Image Frame (16:9 Aspect Ratio) */}
              <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden rounded-2xl border border-border/60 bg-muted shadow-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_renovation_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_renovation_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_renovation_hero.png-wvc-sizes" alt="Sunlit architectural interior remodel in Brookhaven featuring white oak cabinetry and honed stone surfaces" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-700 ease-out hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
              </div>

              {/* Floating Atelier Frosted Glass Card */}
              <motion.div animate={shouldReduceMotion ? {
              y: 0
            } : {
              y: [0, -6, 0]
            }} transition={shouldReduceMotion ? {} : {
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }} className="mt-4 sm:mt-0 sm:absolute sm:-bottom-6 sm:-left-6 sm:max-w-xs md:max-w-sm rounded-2xl border border-white/80 bg-card/85 p-5 md:p-6 shadow-xl backdrop-blur-md transition-[border-color,box-shadow] hover:border-primary/40">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-secondary">
                    <Award className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-foreground/70">
                      Signature Standard
                    </p>
                    <p className="font-serif text-base font-semibold text-foreground">
                      Full-Service General Contracting
                    </p>
                  </div>
                </div>

                <div className="mt-3.5 pt-3.5 border-t border-border/60 flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-xs text-foreground/80">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>Historic &amp; Modern Neighborhood Homes</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground/80">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>End-to-End Brookhaven Permitting &amp; Design</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}